import { api } from '../api/endpoints'
import type { ChannelEvent, Message } from '../api/types'
import { db } from '../db/db'
import { isOnline } from '../state/connection'

// Sync sends only the people its response names (contract: Sync), so a channel's quieter members, or someone whose first message arrives
// live, may not be on this device yet. These fetch them by id when something needs them.

/** The directory endpoint takes at most this many ids per request. */
const BATCH = 500
const inflight = new Set<string>()

/** Fetches, once, the users among these ids that this device does not have. Never throws: a name shows once it arrives. */
export async function ensureUsers(ids: Iterable<string | null | undefined>): Promise<void> {
  const wanted = [...new Set([...ids].filter((id): id is string => Boolean(id)))].filter((id) => !inflight.has(id))
  if (wanted.length === 0 || !isOnline()) return
  const known = await db.users.bulkGet(wanted)
  const missing = wanted.filter((_, i) => !known[i])
  if (missing.length === 0) return
  for (const id of missing) inflight.add(id)
  try {
    for (let i = 0; i < missing.length; i += BATCH) {
      const found = await api.usersByIds(missing.slice(i, i + BATCH))
      if (found.length) await db.users.bulkPut(found)
    }
  } catch {
    // Offline or refused: the next caller tries again.
  } finally {
    for (const id of missing) inflight.delete(id)
  }
}

/** Test seam. */
export function resetEnsureUsers(): void {
  inflight.clear()
}

/** Everyone a message shows: its author and the people who reacted. */
export function messageUserIds(message: Pick<Message, 'authorId' | 'reactions'>): string[] {
  return [message.authorId, ...message.reactions.flatMap((r) => r.userIds)]
}

/** Everyone an event names: its actor, a new message's author, the people added, the person removed or reacting. */
export function eventUserIds(event: ChannelEvent): string[] {
  const ids = [event.actorId]
  switch (event.type) {
    case 'message.created':
      ids.push(...messageUserIds(event.payload.message))
      break
    case 'member.added':
      ids.push(...event.payload.members.map((m) => m.userId))
      break
    case 'member.removed':
    case 'member.role_changed':
    case 'reaction.added':
    case 'reaction.removed':
      ids.push(event.payload.userId)
      break
  }
  return ids
}
